import { changed, noOp, rejected, type Command, type CommandExecution } from './commands';
import { createId, type EditorDocument, type Guide } from './model';

export interface GuideLayoutSettings {
  columns: number;
  rows: number;
  columnGutter: number;
  rowGutter: number;
  marginTop: number;
  marginRight: number;
  marginBottom: number;
  marginLeft: number;
  replaceExisting: boolean;
}

function finiteNonNegative(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function axisGuides(axis: Guide['axis'], length: number, count: number, gutter: number, start: number, end: number): Guide[] {
  const boundedCount = Math.max(1, Math.min(24, Math.round(count)));
  const boundedGutter = finiteNonNegative(gutter);
  const available = length - start - end - boundedGutter * (boundedCount - 1);
  if (available < 0) throw new Error(`Margins and ${axis === 'vertical' ? 'column' : 'row'} gutters exceed the document.`);
  const cell = available / boundedCount;
  const positions = new Set<number>([start, length - end]);
  for (let index = 1; index < boundedCount; index += 1) {
    const leading = start + cell * index + boundedGutter * (index - 1);
    positions.add(leading);
    positions.add(leading + boundedGutter);
  }
  return [...positions]
    .filter((position) => position >= 0 && position <= length)
    .sort((left, right) => left - right)
    .map((position) => ({ id: createId('guide'), axis, position }));
}

export function createGuideLayout(width: number, height: number, settings: GuideLayoutSettings, existing: readonly Guide[] = []): Guide[] {
  const left = finiteNonNegative(settings.marginLeft);
  const right = finiteNonNegative(settings.marginRight);
  const top = finiteNonNegative(settings.marginTop);
  const bottom = finiteNonNegative(settings.marginBottom);
  if (left + right > width || top + bottom > height) throw new Error('Guide margins exceed the document.');
  const generated = [
    ...axisGuides('vertical', width, settings.columns, settings.columnGutter, left, right),
    ...axisGuides('horizontal', height, settings.rows, settings.rowGutter, top, bottom),
  ];
  return [...(settings.replaceExisting ? [] : existing.map((guide) => ({ ...guide }))), ...generated];
}

export class GuideLayoutCommand implements Command {
  readonly label = 'New guide layout';
  private before?: Guide[];
  private after?: Guide[];
  /** Whether the document had a guides list before; undo restores its absence byte for byte. */
  private hadGuides?: boolean;

  constructor(private readonly settings: GuideLayoutSettings) {}

  retainedBytes(): number {
    return 256 + JSON.stringify(this.before ?? []).length * 2 + JSON.stringify(this.after ?? []).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    try {
      this.hadGuides ??= document.guides !== undefined;
      this.before ??= (document.guides ?? []).map((guide) => ({ ...guide }));
      this.after ??= createGuideLayout(document.width, document.height, this.settings, this.before);
      if (JSON.stringify(document.guides ?? []) === JSON.stringify(this.after)) return noOp();
      document.guides = this.after.map((guide) => ({ ...guide }));
      return changed();
    } catch (reason) {
      return rejected(reason instanceof Error ? reason.message : 'Guide layout is invalid.');
    }
  }

  undo(document: EditorDocument): void {
    if (this.hadGuides === false) delete document.guides;
    else document.guides = this.before?.map((guide) => ({ ...guide })) ?? [];
  }

  redo(document: EditorDocument): void {
    document.guides = this.after?.map((guide) => ({ ...guide })) ?? [];
  }
}
